// `/meus-quadrinhos/:id/editar` e `/meus-quadrinhos/rascunho` — O EDITOR, COM ENDEREÇO.
//
// São duas rotas e um componente porque o editor abre de dois jeitos, e a diferença não é de
// tela: é de o quadrinho existir ou não no banco.
//
//   /meus-quadrinhos/:id/editar   um quadrinho salvo, achado pelo id
//   /meus-quadrinhos/rascunho     um quadrinho que ainda não foi salvo, e por isso não tem id
//
// O rascunho chega pelo `state` da navegação, com o formato escolhido em `/meus-quadrinhos/novo`.
// Quem cair em `/meus-quadrinhos/rascunho` sem esse estado — recarregando a página, ou colando o
// endereço — volta para a escolha de formato, porque não há rascunho nenhum para restaurar. Isso
// é honesto: o quadrinho não salvo não existe em lugar nenhum, e fingir que existe seria abrir
// uma folha em branco diferente da que a pessoa tinha.
//
// ── T2.44, E O QUE ESTA ROTA RESOLVE DELE ───────────────────────────────────────────────────
//
// O ponto diz "quando aperta voltar, eu perco tudo". Com uma rota só, "voltar" saía do
// `/dashboard` inteiro: a pessoa ia parar na home, e as três telas — balões, lista, editor —
// desapareciam juntas.
//
// Agora "voltar" do editor cai no acervo, que é onde ela estava.
//
// E a segunda metade — AVISAR antes de descartar alteração não salva — foi feita depois, e está
// mais abaixo neste arquivo. Ela exigiu o `Comic` passar a saber que foi alterado: `isSaved()`
// respondia "tem id", não "está em dia", e um quadrinho salvo ontem e editado agora respondia
// `true`.
//
// (Este comentário já disse, por algumas horas, que a segunda metade "fica para o TS-T2.6.5".
// Ficou e voltou no mesmo dia; a linha está corrigida porque comentário que descreve um estado
// vencido é o mesmo defeito que um `oQueFalta` vencido no EPICO — e este ticket já pagou por
// aquele.)

import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import GuardaDoAcervo from "./guarda";
import Nav from "../../components/nav";
import "../nao-encontrado/nao-encontrado.scss";
import CreateComic from "../criar-comic/criar-comic";
import { Comic } from "../../classes/comic.class";
import { comicComGradeInicial } from "../../classes/grade-inicial";
import { FormatoPagina, PADROES_PAGINA } from "../../api/documento.types";
import AppUser from "../../api/app-user.class";
import { GlobalState } from "../../global-state-context";
import { AVISO_DE_SAIDA, podeSair, registrarGuardaDeSaida } from "./saida-sem-salvar";
import { ESPERA_DO_ACERVO_MS } from "./quadrinho-do-acervo";

// O NÚMERO MORA NUM LUGAR SÓ, desde 27/09: a tela de publicar precisou do mesmo tempo de espera, e
// duas telas com o mesmo número em dois arquivos é o jeito de uma delas mudar sozinha. O porquê do
// oito está em `quadrinho-do-acervo.ts`, junto com a decisão que as três rotas compartilham.

/** É um formato conhecido? Vem do `state` da navegação, que é dado de fora e não se confia. */
function formatoValido(v: unknown): v is FormatoPagina {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(PADROES_PAGINA, v);
}

function Editor({
  globalState,
  appUser,
}: {
  globalState: GlobalState;
  appUser: AppUser;
}) {
  const { id } = useParams();
  const local = useLocation();
  const navigate = useNavigate();
  const ehRascunho = !id;

  // O editor MUTA o quadrinho em memória. Por isso ele recebe uma cópia, e a cópia é feita uma
  // vez — refazê-la a cada render descartaria o trabalho a cada tecla.
  const [comic, setComic] = useState<Comic | null | undefined>(undefined);

  // A LISTA, E NÃO O `appUser`. O acervo chega por snapshot e `setComics` devolve um array
  // NOVO a cada chegada; o `appUser` é sempre o mesmo objeto, mutado por dentro. Depender do
  // objeto é depender de algo que nunca muda — foi isso que travou o T8.4.
  const comics = appUser.Comics();

  // Qual id já está na mão. Serve para distinguir "ainda não chegou" de "sumiu da lista".
  const idCarregado = useRef<string | null>(null);

  /**
   * T2.44 · O MENU PASSA A PERGUNTAR ANTES DE SAIR.
   *
   * Medido em 27/09: com alteração registrada (2 formas no palco, "Desfazer" destravado), clicar em
   * "Meus quadrinhos" no menu **não perguntava nada** e saía. A guarda existia numa saída — a do botão
   * de voltar do próprio editor — e sair tem três portas.
   *
   * O `comicAgora` é lido por referência e não por fechamento: a guarda vive enquanto esta tela vive, e
   * um fechamento sobre o `comic` do primeiro render perguntaria sobre o quadrinho de antes.
   */
  const comicAgora = useRef(comic);
  comicAgora.current = comic;
  useEffect(
    () =>
      registrarGuardaDeSaida(() => {
        const c = comicAgora.current;
        if (!c || !c.temAlteracaoNaoSalva()) return true;
        // ESTA E A UNICA CHAMADA DE DIALOGO NATIVO QUE SOBROU NO PRODUTO, e o motivo: a versao do
        // React Router aqui nao tem bloqueio de navegacao, entao a decisao TEM que ser sincrona. Um
        // modal do produto obrigaria a interromper a navegacao, guardar o destino, esperar a resposta
        // e renavegar, e cada uma dessas e um caminho que alguem esquece — e o esquecido perde o
        // trabalho da crianca, que e o defeito que esta guarda existe para impedir.
        //
        // O porque longo, com as tres portas de saida, esta em `saida-sem-salvar.ts`.
        return window.confirm(AVISO_DE_SAIDA);
      }),
    []
  );

  useEffect(() => {
    if (ehRascunho) {
      const estado = local.state as { formato?: unknown; nome?: unknown } | null;
      const formato = estado?.formato;
      if (!formatoValido(formato)) {
        navigate("/meus-quadrinhos/novo", { replace: true });
        return;
      }
      // T8.29 · o nome vem da tela de formato. `typeof` porque `state` é dado de fora e não se confia.
      const nome = typeof estado?.nome === "string" ? estado.nome : undefined;
      setComic(comicComGradeInicial(formato, nome));
      return;
    }

    // ── T8.4 ───────────────────────────────────────────────────────────────────────────
    //
    // "abrir a mesma URL diretamente (F5, link salvo, nova aba) mostra uma página sem estilo
    //  nenhum dizendo que o quadrinho não existe, mesmo com ele existindo."
    //
    // MEDIDO EM PRODUÇÃO EM 24/09/2026, e o achado é pior do que uma corrida: 14 amostras ao
    // longo de 9,8 s num quadrinho que existe, TODAS dizendo "Não encontrei esse quadrinho".
    // Não resolvia tarde — não resolvia nunca.
    //
    // A CAUSA ESTAVA NESTAS DUAS LINHAS, e são duas coisas somadas:
    //
    //   1. `appUser.Comics()` era lido UMA VEZ, na montagem. O acervo chega por snapshot do
    //      Firestore, depois; até chegar, a lista é `[]` — e `[]` respondia "não existe".
    //   2. o efeito não voltava a rodar quando ele chegava: `appUser` é o MESMO objeto, mutado
    //      em `appUser.Comics(comics)`, então a identidade nunca muda e a dependência nunca
    //      dispara.
    //
    // A tela de VISUALIZAR acerta por acidente de forma: ela lê `Comics()` durante o render,
    // então reencontra o quadrinho a cada repintura. O editor não pode fazer o mesmo — ele
    // MUTA uma cópia, e refazê-la a cada render descartaria o desenho a cada tecla.
    //
    // Então o conserto é: depender da LISTA (que troca de identidade a cada snapshot), não do
    // `appUser`; e só concluir "não existe" quando já houve tempo de o acervo chegar. Enquanto
    // não houve, a tela diz que está abrindo — silêncio não é resposta.
    const achado = comics.find((c) => c.getId() === id);
    if (achado) {
      idCarregado.current = id ?? null;
      setComic(achado.copy());
      return;
    }

    // Já tínhamos este quadrinho na mão e ele sumiu da lista (excluído noutra aba): aí sim é
    // "não existe", e imediatamente.
    if (idCarregado.current === id) {
      setComic(null);
      return;
    }

    // Ainda não achei, e nunca achei. Espero o acervo — com teto, porque esperar para sempre
    // é a outra forma de não dar resposta, e é o defeito do T8.12 noutra tela.
    const conta = setTimeout(() => setComic(null), ESPERA_DO_ACERVO_MS);
    return () => clearTimeout(conta);
  }, [id, ehRascunho, local.state, comics, navigate]);

  // ── A SEGUNDA METADE DO T2.44 ──────────────────────────────────────────────────────────
  //
  // "quando aperta voltar, EU PERCO TUDO."
  //
  // A primeira metade foi o TS-T2.6.2: com `/dashboard` quebrado em rotas, voltar do editor cai
  // no acervo em vez de sair da area inteira. A metade que faltava era AVISAR, e ela exigiu o
  // `Comic` passar a saber que foi alterado (`temAlteracaoNaoSalva`) — `isSaved()` respondia "tem
  // id", nao "esta em dia".
  //
  // SAO DOIS CAMINHOS DE SAIDA, e eles precisam de mecanismos diferentes:
  //
  //   fechar a aba / recarregar  ->  `beforeunload`, o unico gancho que o navegador da. A
  //                                  mensagem e do navegador, nao nossa; o que o site controla e
  //                                  SE ela aparece.
  //   navegar dentro do site     ->  `confirm`, porque o React Router v6 desta versao nao expoe
  //                                  bloqueio de navegacao (`useBlocker` so existe no data
  //                                  router). Esta no `setCurrentComic` passado ao editor.
  useEffect(() => {
    if (!comic) return;
    const avisar = (e: BeforeUnloadEvent) => {
      if (!comic.temAlteracaoNaoSalva()) return;
      // `preventDefault` + `returnValue` porque navegadores diferentes ainda olham um ou o outro.
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [comic]);

  // Enquanto o acervo não chegou, a tela DIZ que está abrindo. Antes ela devolvia `<></>` —
  // uma tela branca —, que para quem está do outro lado é indistinguível de travamento. É o
  // mesmo defeito que o T8.12 tem na biblioteca, e a lição é a mesma: ausência não é estado.
  if (comic === undefined) {
    return (
      <div className="ne-container">
        <Nav />
        <main className="ne-conteudo">
          <h1>Abrindo o seu quadrinho…</h1>
          <p className="ne-explica">Buscando no seu acervo. Isto leva um instante.</p>
        </main>
      </div>
    );
  }

  // Id que não existe no acervo desta pessoa. Não distingo "não existe" de "não é seu" de
  // propósito: as duas respostas são a mesma para quem pergunta, e diferenciá-las contaria a
  // quem adivinha um id que aquele id existe.
  if (comic === null) {
    return (
      <div className="ne-container">
        <Nav />
        <main className="ne-conteudo">
          <h1>Não encontrei esse quadrinho</h1>
          <p className="ne-tentado">
            Você tentou abrir <code>{local.pathname}</code>
          </p>
          <p className="ne-explica">
            Ele pode ter sido excluído, ou o endereço pode estar errado. Nada do que você criou
            se perde por causa disto — seu acervo continua em "Meus quadrinhos".
          </p>
          <div className="ne-saidas">
            <Link className="ne-botao ne-botao-forte" to="/meus-quadrinhos">
              Meus quadrinhos
            </Link>
            <Link className="ne-botao" to="/biblioteca">
              Biblioteca
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <CreateComic
      globalState={globalState}
      currentComic={comic}
      // T8.20 · a URL deixa de mentir na primeira vez que o quadrinho ganha id. `replace` e nao
      // `push`: o endereco /rascunho ja nao mostra este quadrinho, e deixa-lo no historico faria o
      // botao Voltar levar a uma tela que manda a pessoa escolher formato de novo.
      aoGanharId={(novoId) => navigate(`/meus-quadrinhos/${novoId}/editar`, { replace: true })}
      setCurrentComic={(c) => {
        // O editor usava este `setState` para duas coisas: trocar o quadrinho em edição e VOLTAR
        // para a lista (`setCurrentComic(null)`). A segunda agora é navegação de verdade — e
        // passa pela guarda do T2.44.
        if (c === null || c === undefined) {
          // T2.44 · ESTA PORTA USA A MESMA GUARDA DO MENU, e não repete a pergunta.
          //
          // Aqui havia um `window.confirm` próprio, com a mesma frase. Um teste meu — o varredor de
          // diálogos nativos — reprovou o commit por isso: ele cobra **uma** chamada nativa no produto,
          // e duas perguntas idênticas em dois lugares é o defeito que este épico passou o dia
          // consertando em outras telas.
          //
          // A pergunta mora na guarda registrada abaixo (`registrarGuardaDeSaida`), que é a mesma que o
          // menu consulta. Quem sai por qualquer porta ouve a mesma frase, uma vez.
          if (!podeSair()) return;
          navigate("/meus-quadrinhos");
        } else setComic(c);
      }}
    />
  );
}

function EditarQuadrinho() {
  return (
    <GuardaDoAcervo>
      {({ globalState, appUser }) => (
        <Editor globalState={globalState} appUser={appUser} />
      )}
    </GuardaDoAcervo>
  );
}

export default EditarQuadrinho;
